import React from 'react';
import { Helmet, HelmetProvider } from "react-helmet-async";

const ICONS = {
  'New features': (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="text-blue-500 w-6 h-6 mr-2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.59 14.37a6 6 0 0 1-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 0 0 6.16-12.12A14.98 14.98 0 0 0 9.631 8.41m5.96 5.96a14.926 14.926 0 0 1-5.841 2.58m-.119-8.54a6 6 0 0 0-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 0 0-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 0 1-2.448-2.448 14.9 14.9 0 0 1 .06-.312m-2.24 2.39a4.493 4.493 0 0 0-1.757 4.306 4.493 4.493 0 0 0 4.306-1.758M16.5 9a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Z" />
    </svg>
  ),
  'Improvements': (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="text-red-500 w-6 h-6 mr-2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.362 5.214A8.252 8.252 0 0 1 12 21 8.25 8.25 0 0 1 6.038 7.047 8.287 8.287 0 0 0 9 9.601a8.983 8.983 0 0 1 3.361-6.867 8.21 8.21 0 0 0 3 2.48Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18a3.75 3.75 0 0 0 .495-7.468 5.99 5.99 0 0 0-1.925 3.547 5.975 5.975 0 0 1-2.133-1.001A3.75 3.75 0 0 0 12 18Z" />
    </svg>
  ),
  'Bug fixes': (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="text-orange-500 w-6 h-6 mr-2">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 12.75c1.148 0 2.278.08 3.383.237 1.037.146 1.866.966 1.866 2.013 0 3.728-2.35 6.75-5.25 6.75S6.75 18.728 6.75 15c0-1.046.83-1.867 1.866-2.013A24.204 24.204 0 0 1 12 12.75Zm0 0c2.883 0 5.647.508 8.207 1.44a23.91 23.91 0 0 1-1.152 6.06M12 12.75c-2.883 0-5.647.508-8.208 1.44.125 2.104.52 4.136 1.153 6.06M12 12.75a2.25 2.25 0 0 0 2.248-2.354M12 12.75a2.25 2.25 0 0 1-2.248-2.354M12 8.25c.995 0 1.971-.08 2.922-.236.403-.066.74-.358.795-.762a3.778 3.778 0 0 0-.399-2.25M12 8.25c-.995 0-1.97-.08-2.922-.236-.402-.066-.74-.358-.795-.762a3.734 3.734 0 0 1 .4-2.253M12 8.25a2.25 2.25 0 0 0-2.248 2.146M12 8.25a2.25 2.25 0 0 1 2.248 2.146M8.683 5a6.032 6.032 0 0 1-1.155-1.002c.07-.63.27-1.222.574-1.747m.581 2.749A3.75 3.75 0 0 1 15.318 5m0 0c.427-.283.815-.62 1.155-.999a4.471 4.471 0 0 0-.575-1.752M4.921 6a24.048 24.048 0 0 0-.392 3.314c1.668.546 3.416.914 5.223 1.082M19.08 6c.205 1.08.337 2.187.392 3.314a23.882 23.882 0 0 1-5.223 1.082" />
    </svg>
  ),
};

const notes = [
    {
    version: '1.51',
    date: 'August 29, 2026',
    sections: [
      {
        heading: 'New features',
        items: [
          "Customer display on a secondary monitor",
          "Change selected item's price on demand",
          "Collected credit payments report",
          "Purchase payment types report"
        ],
      },
      {
        heading: 'Bug fixes',
        items: [
          "Re-printing a historical Z-report printed today's date instead of the original report date",
          "Fixed an issue that could cause the app to close unexpectedly while updating customer details"
        ],
      },
    ],
  },
  {
    version: '1.50.1.3',
    date: 'July 19, 2026',
    sections: [
      {
        heading: 'Improvements',
        items: [
          "Added an option to save Z reports as PDF from the End of day History tab"
        ],
      },
      {
        heading: 'Bug fixes',
        items: [
          "Corrected an issue where emailed Z reports included data outside the current business day",
          "Resolved an issue that prevented Z reports from being sent at the end of the day on some setups",
          "Fixed an issue where the Purchase invoice list report did not reflect the selected period"
        ],
      },
    ],
  },
  {
    version: '1.50.1.2',
    date: 'July 10, 2026',
    sections: [
      {
        heading: 'Bug fixes',
        items: [
          "Fixed a Z-report printing issue when using A4 printer",
          "Resolved an issue where the outstanding balance was calculated incorrectly after processing a refund"
        ],
      },
    ],
  },
  {
    version: '1.50.1.1',
    date: 'July 6, 2026',
    sections: [
      {
        heading: 'Bug fixes',
        items: [
          "Fixed a receipt printing issue when using the Dual Currency plugin",
        ],
      },
    ],
  },
  {
    version: '1.50.1.0',
    date: 'July 1, 2026',
    sections: [
      {
        heading: 'New features',
        items: [
          "Allow users to retry database update when failure happens on startup",
        ],
      },
    ],
  },
  {
    version: '1.50.0.1',
    date: 'June 23, 2026',
    sections: [
      {
        heading: 'Bug fixes',
        items: [
          "Resolved a crash when searching products from main screen",
        ],
      },
    ],
  },
  {
    version: '1.50.0.0',
    date: 'June 22, 2026',
    sections: [
      {
        heading: 'New features',
        items: [
          "Configure custom rounding for orders and documents",
          "Merge duplicate products to simplify item management",
          "Back up Aronium Pro databases from Database setup application",
          "Show the selected customer name in the open order list",
          "Stock quantity reports now include an active item filter"
        ],
      },
      {
        heading: 'Improvements',
        items: [
          "Added a VAT column to the Purchase Invoice List report",
          "Product Report now includes a tax amount column",
          "Print customer code on A5 invoices",
          "Printed Purchase documents (Goods Receipt) now include unit of measurement",
          "Improved the message shown when deleting customers with linked documents",
          "Retrying database update in case of failure",
          "Display outstanding customer balance on the payment screen",
          "Updated Estonian translation"
        ],
      },
      {
        heading: 'Bug fixes',
        items: [
          "Fixed A4 refund invoice printing",
          "Restored print station printing when auto-logout is enabled",
          "Prevented users from clocking out from User Info while open orders still exist"
        ],
      },
    ],
  },
  {
    version: '1.49.2.1',
    date: 'April 28, 2026',
    sections: [
      {
        heading: 'Bug fixes',
        items: [
          "Fixed an issue with Payment types report"
        ],
      },
    ],
  },
  {
    version: '1.49.2',
    date: 'April 25, 2026',
    sections: [
      {
        heading: 'New features',
        items: [
          "Schedule automatic database backups at specified intervals",
          "Create automatic database backups with Aronium Pro"
        ],
      },
      {
        heading: 'Improvements',
        items: [
          <>
            Migrate floor plans with <a href="https://help.aronium.com/hc/en-us/articles/360016060619-Data-migration" className="text-blue-600 hover:underline">Data Migration</a> tool
          </>,
          "Simplified permission granting for the database backup directory in Aronium Pro"
        ],
      },
      {
        heading: 'Bug fixes',
        items: [
          "Fixed an application crash that occurred when the selected order did not exist (Aronium Pro)",
          "Fixed an application crash caused by virtual keyboard failure",
          "Fixed an application crash that occurred when saving a report to a file already open in another application",
          "Fixed an issue where users could not save credit payments because of an amount mismatch",
          "Fixed an issue where product groups were truncated in the Reporting filter dropdown",
          "Fixed an issue where users could not use the age restriction validation if Management modules were not installed (Aronium Pro)"
        ],
      },
    ],
  },
  {
    version: '1.49.1',
    date: 'January 17, 2026',
    sections: [
      {
        heading: 'Improvements',
        items: [
          "Added clearer error messaging when an unexpected issue occurs, so you know why the application stopped",
          "When the Dual Currency plugin is installed, Aronium now shows change amounts in the second currency as well"
        ],
      },
      {
        heading: 'Bug fixes',
        items: [
          "Fixed a crash when loading the configuration file while it was being used by another process",
          "Improved stability by preventing the application from closing unexpectedly after unhandled errors are detected",
          "Fixed a crash caused by a divide-by-zero scenario when using a weighing scale",
          "Added a clear warning and recovery options when required database migrations can't be executed",
          "Fixed a crash when saving a report to a file that is currently open in another application",
          "Fixed a crash when Dual Currency is installed and the exchange rate is set to zero",
          "Fixed a crash when opening a hyperlink on computers without a default browser/protocol set"
        ],
      },
    ],
  },
  {
    version: '1.49',
    date: 'December 20, 2025',
    sections: [
      {
        heading: 'New features',
        items: [
          "Better handling of subscriptions that are inactive or blocked",
          "In Management and Sales history, invoices now show the total before tax and the tax amount"
        ],
      },
      {
        heading: 'Improvements',
        items: [
          "Update of Estonian translation",
          "Added checks to help prevent incorrect payment methods during credit reconciliation"
        ],
      },
      {
        heading: 'Bug fixes',
        items: [
          "Service type (Dine-in or Takeaway) not printed when \"Rich text\" is enabled on Generic Text Only kitchen printer",
          "Incorrect average cost calculation when adding multiple lines of same product in a Purchase document",
          "Fixed discount rounding being limited to 2 decimals even when the system and currency use more decimal places"
        ],
      },
    ],
  },
  {
    version: '1.48.0.1',
    date: 'November 15, 2025',
    sections: [
      {
        heading: 'Bug fixes',
        items: [
          'Application crashes in certain scenarios when selecting an item from the open orders list',
        ],
      },
    ],
  },
  {
    version: '1.48',
    date: 'November 8, 2025',
    sections: [
      {
        heading: 'New features',
        items: [
          "Flag Dine-in or Takeaway order Plugin",
          "Fatoora Standard B2B Invoices Support",
          "Aronium Pro: Upgrade SQL Server version to 2019",
          "Added Azərbaycan translation",
          "Print large order number on receipts",
        ],
      },
      {
        heading: 'Improvements',
        items: [
          "Update of Estonian translation",
        ],
      },
      {
        heading: 'Bug fixes',
        items: [
          "Credit Payment Receipt shows Company Details instead of Customer Details",
          "Application crashes if number of decimal places is out of range",
          "Customer information gets lost when saved order is reloaded",
          "Aronium Pro: Fatoora indicator for unresolved invoices is not shown to user",
          "Fatoora: Cannot print invoices created before Fatoora plugin installation",
          "Fatoora: Tax code prints incorrectly when using RTL"
        ],
      },
    ],
  },
  {
    version: '1.47',
    date: 'August 5, 2025',
    sections: [
      {
        heading: 'New features',
        items: [
          "Automatic plugin updates",
          "Added Spanish (Colombia) language",
          "Report search functionality",
          "Stock returns report",
          "Transaction history report",
        ],
      },
      {
        heading: 'Improvements',
        items: [
          "Ability to print logo at full width on receipt",
          "Added document notes in the credit payments list",
          "Payment types included in the invoice list report",
        ],
      },
      {
        heading: 'Bug fixes',
        items: [
          "Aronium Pro: Minimum Retail Price (MRP) plugin causes application crash when entering Settings in certain scenarios",
          "Fatoora: Documents are not signed if Management modules are not installed",
          "Unable to save report to file due to forward slash in the file path",
        ],
      },
    ],
  },
  {
    version: '1.46.0.2',
    date: 'June 21, 2025',
    sections: [
      {
        heading: 'Bug fixes',
        items: [
          'Application crashes in Visual layout if there are no action buttons enabled in button bar',
        ],
      },
    ],
  },
  {
    version: '1.46.0.1',
    date: 'June 16, 2025',
    sections: [
      {
        heading: 'Bug fixes',
        items: [
          'Application crashes in Visual layout if there are no quick payment types',
        ],
      },
    ],
  },
  {
    version: '1.46',
    date: 'June 8, 2025',
    sections: [
      {
        heading: 'New features',
        items: [
          'Indicator for unsubmitted invoices in Fatoora plugin for Saudi Arabia',
          'Chinese translation',
        ],
      },
      {
        heading: 'Improvements',
        items: [
          'Display more payment types in Visual layout',
          'Limited order name to 100 characters when using Named order plugin',
        ],
      },
      {
        heading: 'Bug fixes',
        items: [
          'Arabic text is not displayed and stored correctly when used in a certificate for Fatoora plugin',
          'Total cost displayed in Stock view even if product cost prices are hidden',
          'Negative total displayed after item deleted in specific setup',
        ],
      },
    ],
  },

  {
    version: '1.45',
    date: 'March 9, 2025',
    sections: [
      {
        heading: 'New features',
        items: [
          'Credit payment reconciliations included in Z report',
          'Added tax number to the Z report header',
        ],
      },
      {
        heading: 'Improvements',
        items: [
          'Updated German language',
          'Updated Estonian language',
          'Updated Total column title in Spanish in Standard layout',
        ],
      },
      {
        heading: 'Bug fixes',
        items: [
          'Expiration date not saved when using advanced "Save" options in a purchase document',
          'Invoices created in management are not included in Z report in Aronium Pro',
          'Tax breakdown border lines duplicated in purchase print',
          'Business day prints default time (00:00:00) in X report',
          'Pressing F8 starts new order even if floor plans are enabled',
          'Email reports are not sent in specified working hours',
          'Incorrect credit balance in report when credit sale is partially refunded',
        ],
      },
    ],
  },

  {
    version: '1.44.0.1',
    date: 'November 28, 2024',
    sections: [
      {
        heading: 'Bug fixes',
        items: [
          'Error during CSV import of products without product code',
          'Aronium Pro crashes after refreshing database list in settings',
          'Aronium Pro crashes when Quick inventory is created for new products',
        ],
      },
    ],
  },

  {
    version: '1.44',
    date: 'November 23, 2024',
    sections: [
      {
        heading: 'New features',
        items: [
          'Track product expiration date',
          'Add details items report so it can be exported and filtered in Excel',
          'Show prices in second currency in totals and products (Visual layout only)',
          'Added Estonian translations',
          'Ability to check database health and recover database for Aronium Pro',
        ],
      },
      {
        heading: 'Improvements',
        items: [
          'Option to disable unused taxes, so they do not show in payment screen',
          'Control access to price lists in Management',
          'Replaced comma with dot in numeric keyboard layout',
          'Fix incorrect stock quantities caused by faulty inventory counts',
        ],
      },
      {
        heading: 'Bug fixes',
        items: [
          'Refresh prices when changing price list on cash register',
          'Arabic payment types displayed incorrectly in sales history and documents view',
          'Incorrect quantity when products are merged in CSV import',
          'Prevent cash drawer to be open using shortcut keys if user is not logged in',
        ],
      },
    ],
  },

  {
    version: '1.43.0.2',
    date: 'May 7, 2024',
    sections: [
      {
        heading: 'Bug fixes',
        items: [
          'Error when creating new products using CSV import',
        ],
      },
    ],
  },

  {
    version: '1.43.0.1',
    date: 'May 5, 2024',
    sections: [
      {
        heading: 'Bug fixes',
        items: [
          'Search text not selected in search box after adding an item',
          'Automatic update won\'t start',
          'Number of pages in stock report printing incorrectly',
          'Start group selected after adding an item with comments',
        ],
      },
    ],
  },

  {
    version: '1.43',
    date: 'May 4, 2024',
    sections: [
      {
        heading: 'New features',
        items: [
          'Support for Dual currency plugin and ability to print total in second currency in receipt',
          'Added new "Night" theme',
        ],
      },
      {
        heading: 'Improvements',
        items: [
          'Show print dialog on refund and make refund print optional',
          'Version check on Aronium Pro will inform users if server was updated to the newer version',
          'Set initial purchase price from cost price when creating new product',
          'Open cash drawer from Cash In/Out screen',
        ],
      },
      {
        heading: 'Bug fixes',
        items: [
          'Overlapping page number in RTL invoice',
          'Application crashes when copying barcode in TagControl',
          'Search box losing focus when navigating between groups in visual layout',
          'Clearing search doesn\'t remove search info in visual layout',
          'Partial refund from a credit sale changes the status of the original sale to "paid"',
          'Aronium Pro installer warning message not visible if screen is scaled to 200%',
          'No void reasons info visible even if predefined void reasons are available',
        ],
      },
    ],
  },

  {
    version: '1.42',
    date: 'March 31, 2024',
    sections: [
      {
        heading: 'New features',
        items: [
          'Support for Multiple price lists plugin',
          'Print unpaid balance in receipt and invoice',
          'Optimize database on Aronium Pro',
          'Take over an existing order from another user',
        ],
      },
      {
        heading: 'Improvements',
        items: [
          'Updated Estonian translation',
          'Use custom labels from receipt in A4 and A5 invoice',
          'Added instructions to configure printer in print dialog if printer is disabled',
          'Memory optimization',
        ],
      },
      {
        heading: 'Bug fixes',
        items: [
          'Fixed validation for existing customer validation',
          'Sounds not playing in Standard layout',
          'Application fails if too long number is entered in product code field',
          'Products sale report is not sorted',
          'Tax list is not scrollable in Tax overrides screen',
          'Negative stock quantity prints incorrectly in stock history report',
          'Application crashing when using unsupported image files',
          'Refund is not calculated in the Tax report',
          'Taxes from proforma appear in Z report in certain scenarios',
          'Company email and tax number are not printed in RTL proforma invoice',
        ],
      },
    ],
  },

  {
    version: '1.41.0.1',
    date: 'November 4, 2023',
    sections: [
      {
        heading: 'Bug fixes',
        items: [
          'Payments are not loaded when editing a document',
          'Address is not showing in search results when searching for a customer',
        ],
      },
    ],
  },
];

const ReleaseNotes = () => {
  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Release notes - Aronium</title>
          <meta name="description" content="Track changes and Aronium release history" />
        </Helmet>
      </HelmetProvider>

      <section className="py-8">
        <div className="max-w-screen-xl mx-auto px-4 text-center">
          <h1 className="text-4xl mb-4">Release notes</h1>
          <p>See what’s new and improved!</p>
        </div>
      </section>
      <section className="py-12 bg-white" id="release-notes-timeline">
        <div className="max-w-screen-xl mx-auto px-14">
          <ol className="relative border-l border-gray-300">
            {notes.map((note, index) => (
              <li key={index} className="mb-12 relative pl-[60px]">
                <div className="relative flex items-center mb-4">
                  <span className="absolute left-[-110px] top-1/2 transform -translate-y-1/2 w-24 h-10 bg-gray-300 text-white font-semibold rounded-full shadow-md flex items-center justify-center text-sm ">
                    {note.version}
                  </span>
                  <h3 className="text-xl font-bold text-gray-400">{note.date}</h3>
                </div>
                <div className="mt-4 space-y-6">
                  {note.sections.map((section, secIdx) => (
                    <div key={secIdx}>
                      <h4 className="inline-flex items-center text-md font-semibold text-gray-700 mb-2">
                        {ICONS[section.heading]}
                        {section.heading}
                      </h4>
                      <ul className="space-y-2 text-base font-normal text-gray-500 list-disc list-inside">
                        {section.items.map((item, itemIdx) => (
                          <li key={itemIdx}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </>
  );
};

export default ReleaseNotes;
